import { css } from 'styled-components';
import { ITextAreaSizeProps, TextAreaSize, TextAreaWidth, ITextAreaCssProps, ITextAreaProps } from './TextArea';
import { IDisplayType, INoLineHeightProps } from '../../fundamentals/typography/typography';
import { PX_TO_REM, PxToRem, SIZES } from '../../fundamentals/sizes/sizes';
import { EoraSpace, EORA_SPACES } from '../../fundamentals/spaces/space';
import { STATUS_COLOURS } from '../../fundamentals/colours/variants';
import { IInvalidProps } from '../../fundamentals/actions';
import { ITypographyCss, useEoraCss } from '../../fundamentals/typography/typographyEora';
import { TYPOGRAPHY } from '../../fundamentals/typography/eoraTypography';

export const TextAreaFontSizes: Record<TextAreaSize, { name: ITypographyCss; lineHeight: number }> = {
  [SIZES.SMALL]: { name: TYPOGRAPHY.PARAGRAPH.SMALL.MEDIUM, lineHeight: 20 },
  [SIZES.MEDIUM]: { name: TYPOGRAPHY.PARAGRAPH.DEFAULT.MEDIUM, lineHeight: 24 },
  [SIZES.LARGE]: { name: TYPOGRAPHY.PARAGRAPH.LARGE.MEDIUM, lineHeight: 28 }
};

export const useTextAreaFontSizeCss = css<ITextAreaSizeProps & IDisplayType & INoLineHeightProps>`
  ${({ textAreaSize }) => useEoraCss(TextAreaFontSizes[textAreaSize!].name, false, TextAreaFontSizes[textAreaSize!].lineHeight)}
`;

const SIDE_PADDINGS: Record<TextAreaSize, EoraSpace> = {
  [SIZES.SMALL]: EORA_SPACES.MEDIUM,
  [SIZES.MEDIUM]: EORA_SPACES.OFFGRID_12,
  [SIZES.LARGE]: EORA_SPACES.LARGE
};

const TEXT_AREA_WIDTHS: Record<TextAreaWidth, PxToRem> = {
  [SIZES.SMALL]: PX_TO_REM[96],
  [SIZES.MEDIUM]: PX_TO_REM[200],
  [SIZES.LARGE]: PX_TO_REM[320],
  [SIZES.X_LARGE]: PX_TO_REM[600]
};

export const useTextAreaPaddingValue = css<ITextAreaSizeProps>`
  ${({ theme, textAreaSize }) => theme.eoraSpaces[SIDE_PADDINGS[textAreaSize!]]}
`;

export const useTextAreaPaddingCss = css<ITextAreaSizeProps>`
  padding-left: ${useTextAreaPaddingValue};
  padding-right: ${useTextAreaPaddingValue};
`;

export const useTextAreaWidthCss = (widthOverride?: PxToRem | string | number) => css<ITextAreaSizeProps & ITextAreaCssProps>`
  ${({ styleWidth }) => css`
    max-width: 100%;
    width: ${widthOverride ?? (typeof styleWidth === 'number' ? `${styleWidth}rem` : TEXT_AREA_WIDTHS[styleWidth!])};
  `};
`;

export const useTextAreaBorderCss = css<IInvalidProps & Pick<ITextAreaProps, 'disabled'>>`
  ${({ theme, disabled, invalid }) =>
    disabled
      ? css`
          border: ${theme.textFieldColours.disabled.border};
        `
      : css`
          border: ${theme.textFieldColours[invalid ? STATUS_COLOURS.ERROR : `default`].border};
        `}
`;

export const useTextAreaPlaceholderCss = css<Pick<ITextAreaProps, 'disabled'>>`
  color: ${({ theme, disabled }) => theme.textFieldColours[disabled ? `disabled` : `placeholder`].text};
`;

export const useTextAreaTextCss = css<Pick<ITextAreaProps, 'disabled'>>`
  color: ${({ theme, disabled }) => theme.textFieldColours[disabled ? `disabled` : `default`].text};
`;
